// 메신저 스티커 피커 J2a — MM 입력창 옆 버튼 주입 + 사용자 본인 MM 세션으로 전송.
// 설계: `patch_mm_sticker_picker_j2_frontend.md`(패널 UI 는 J2b `StickerPicker`).
//
// 🔴 MM DOM 은 MM 소유다 — 셀렉터가 어긋나거나 오류가 나면 조용히 무동작(지금 화면 그대로).
// 🔴 MM 에는 «사용자 본인 세션(쿠키)»으로만 쓴다 — 게이트웨이 토큰·봇 토큰을 MM 요청에 싣지 않는다.
// 🔴 MMCSRF 는 `Path=/mm` 라 부모 `document.cookie` 에 안 보인다 — iframe 문서에서 읽는다.
//
// 🔴 재시도는 없다(이중 게시 방지). 실패는 문구로 알리고 끝낸다 — 다시 보낼지는 사용자가 정한다.
// (2026-10-16) 피커는 sendSticker(즉시 게시) 대신 stageSticker(입력창 위 첨부 대기열에 올리기)를 부른다 —
// 게시는 사용자가 엔터/전송으로 한다. sendSticker 는 남겨 두지만 피커가 부르지 않는다.
export const BUTTON_ANCHOR_SEL = '#fileUploadButton';
// 📎 와 같은 «액션 줄»에 있는 이웃. 이 둘의 공통 조상이 곧 그 줄이다(입력 영역 전체가 아니라).
export const EMOJI_ANCHOR_SEL = '#emojiPickerButton';
export const TEXTBOX_SEL = '#post_textbox';   // 중앙 입력창(스레드 입력창은 reply_textbox)
export const BUTTON_ID = 'gw-sticker-btn';
export const MARK_ATTR = 'data-gw-sticker-btn';
export const OBSERVE_DEBOUNCE_MS = 150;
export const MM_API_PREFIX = '/mm/api/v4/';
// #post_textbox 에서 위로 올라가며 앵커를 찾을 최대 깊이. 🔴 body 까지 올라가지 않는다 —
// 문서 전체를 뒤지면 스레드(RHS) 입력창의 같은 id 를 잡아 엉뚱한 자리에 붙는다.
export const MAX_ANCHOR_DEPTH = 10;

const MSG_NO_SESSION = '메신저 로그인 정보를 읽지 못했습니다 — 메신저를 새로고침해 주세요.';
const MSG_NO_CHANNEL = '채널이나 대화방을 연 상태에서 보내 주세요.';
const MSG_UNSUPPORTED = '이 대화방은 아직 스티커를 보낼 수 없습니다.';
const MSG_EXPIRED = '메신저 세션이 만료됐습니다 — 메신저를 새로고침해 주세요.';
const MSG_FORBIDDEN = '이 채널에 쓸 권한이 없습니다.';
const MSG_TOO_LARGE = '스티커 파일이 메신저 업로드 한도를 넘습니다.';
const MSG_NETWORK = '스티커를 보내지 못했습니다 (네트워크).';
const MSG_FAILED = '스티커를 보내지 못했습니다.';

// 사용자에게 보일 문구를 들고 다니는 오류. J2b 패널이 instanceof 로 «우리가 만든 실패»를 가린다.
export class StickerSendError extends Error {
  constructor(status, userMessage) {
    super(userMessage || MSG_FAILED);
    this.name = 'StickerSendError';
    this.status = Number(status) || 0;
    this.userMessage = userMessage || MSG_FAILED;
    // 🔴 ES5 로 내려간 빌드에서 `extends Error` 는 instanceof 를 깨뜨린다 — 프로토타입을 되돌린다.
    try { Object.setPrototypeOf(this, StickerSendError.prototype); } catch (e) { /* */ }
  }
}

const TEAM_RE = /^[a-z0-9-]+$/;
const NAME_RE = /^[a-z0-9._-]+$/;
const EMAIL_RE = /^[a-z0-9._+@-]+$/;

// MM 라우터와 같게 소문자로 정규화한 뒤 형식을 본다. `.`·`..` 은 경로 조각으로 쓰면 안 된다.
function ident(raw, re) {
  const value = String(raw === undefined || raw === null ? '' : raw).toLowerCase();
  if (!value || value === '.' || value === '..') return '';
  return re.test(value) ? value : '';
}

// 지금 열려 있는 방이 무엇인지 «주소»에서만 읽는다(MM Redux store·전역 객체를 건드리지 않는다).
// 🔴 규칙은 MM 웹앱 라우터 그대로다: messages/ 아래 26자 = 상대 사용자 id, 40자 = 그룹 DM
//    «채널 이름»(둘 다 채널 id 가 아니다).
export function parseMmChannelPath(pathname) {
  let raw = '';
  try { raw = String(pathname === undefined || pathname === null ? '' : pathname); } catch (e) { return null; }
  raw = raw.split('#')[0].split('?')[0];
  if (raw.indexOf('/mm/') !== 0) return null;
  const seg = raw.split('/');                                   // ['', 'mm', team, kind, rest]
  while (seg.length && seg[seg.length - 1] === '') seg.pop();   // 끝 '/' 무시
  if (seg.length !== 5) return null;
  const team = ident(seg[2], TEAM_RE);
  if (!team) return null;
  const kind = seg[3];
  const rest = seg[4];
  if (kind === 'channels') {
    const name = ident(rest, NAME_RE);
    return name ? { kind: 'channel', team, name } : null;
  }
  if (kind !== 'messages') return null;                         // threads·drafts·pl 등은 방이 아니다
  if (rest.charAt(0) === '@') {
    const username = ident(rest.slice(1), NAME_RE);
    return username ? { kind: 'dm_username', team, username } : null;
  }
  if (rest.indexOf('@') > 0) {
    const email = ident(rest, EMAIL_RE);
    return email ? { kind: 'dm_email', team, email } : null;
  }
  if (rest.length === 26) {
    const userId = ident(rest, NAME_RE);
    return userId ? { kind: 'dm_userid', team, userId } : null;
  }
  if (rest.length === 40) {
    const name = ident(rest, NAME_RE);
    return name ? { kind: 'group', team, name } : null;
  }
  return null;
}

// 🔴 첫 번째 MMCSRF 를 쓴다 — 옛 `Path=/` 쿠키가 남아 둘이 보일 수 있고, 경로가 긴 `/mm` 쿠키가 앞에 온다.
// 🔴 doc.cookie getter 가 던지면(교차 출처 접근 실패) 그대로 올린다 — sendSticker 가 «그 밖»으로 감싼다.
export function readMmCsrf(doc) {
  const raw = String((doc && doc.cookie) || '');
  const parts = raw.split(';');
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i].trim();
    const eq = part.indexOf('=');
    if (eq <= 0) continue;
    if (part.slice(0, eq).trim() === 'MMCSRF') return part.slice(eq + 1).trim();
  }
  return '';
}

function windowFetch() {
  try {
    if (typeof window !== 'undefined' && typeof window.fetch === 'function') return window.fetch.bind(window);
  } catch (e) { /* */ }
  return null;
}

// 🔴 `/mm/api/v4/` 밖으로는 한 글자도 새지 않게 한다 — `..`·`//` 는 프록시 앞단에서 다른 곳을 가리킨다.
function pathIsSafe(path) {
  if (path.indexOf(MM_API_PREFIX) !== 0) return false;
  if (path.indexOf('//') >= 0) return false;
  if (path.indexOf('/./') >= 0 || path.indexOf('/../') >= 0) return false;
  return !/\/\.\.?($|\?)/.test(path);
}

// MM 호출의 단일 통로. credentials=include(본인 쿠키) + XRW 항상 + POST 면 CSRF.
export function mmFetch(fetchImpl, csrf, path, init) {
  const target = String(path === undefined || path === null ? '' : path);
  if (!pathIsSafe(target)) throw new Error('mmFetch: 허용되지 않은 경로 — ' + target);
  const opts = init || {};
  const method = String(opts.method || 'GET').toUpperCase();
  const headers = Object.assign({}, opts.headers || {}, { 'X-Requested-With': 'XMLHttpRequest' });
  if (method === 'POST') headers['X-CSRF-Token'] = csrf || '';
  const fn = typeof fetchImpl === 'function' ? fetchImpl : windowFetch();
  if (!fn) throw new Error('mmFetch: fetch 를 쓸 수 없습니다');
  return fn(target, Object.assign({}, opts, { method, headers, credentials: 'include' }));
}

function statusMessage(status) {
  if (status === 401) return MSG_EXPIRED;
  if (status === 403) return MSG_FORBIDDEN;
  if (status === 413) return MSG_TOO_LARGE;
  return '스티커를 보내지 못했습니다 (HTTP ' + status + ').';
}

// 🔴 «네트워크 오류»는 fetch 가 reject 한 경우만이다 — 두 인자 then 으로 갈라서, 성공 분기에서
//    난 예외(HTTP 오류·JSON 파싱)가 네트워크 문구로 둔갑하지 않게 한다.
function mmOk(fetchImpl, csrf, path, init, notFoundMessage) {
  return mmFetch(fetchImpl, csrf, path, init).then(
    (res) => {
      if (!res) throw new StickerSendError(0, MSG_NETWORK);
      const status = Number(res.status) || 0;
      const ok = (res.ok === undefined || res.ok === null) ? (status >= 200 && status < 300) : !!res.ok;
      if (ok) return res;
      if (status === 404 && notFoundMessage) throw new StickerSendError(status, notFoundMessage);
      throw new StickerSendError(status, statusMessage(status));
    },
    () => { throw new StickerSendError(0, MSG_NETWORK); },
  );
}

function mmJson(fetchImpl, csrf, path, init, notFoundMessage) {
  return mmOk(fetchImpl, csrf, path, init, notFoundMessage).then((res) => res.json());
}

function idOf(payload, failMessage) {
  const id = payload && payload.id;
  if (typeof id === 'string' && id) return id;
  throw new StickerSendError(0, failMessage);
}

// 경로에서 읽은 방 → 채널 id. 🔴 csrf 는 설계의 2-인자 호출을 깨지 않는 «선택» 3번째 인자다
// (POST /channels/direct 에 X-CSRF-Token 이 필요하다).
export function resolveChannelId(parsed, fetchImpl, csrf) {
  const token = csrf || '';
  if (!parsed) return Promise.reject(new StickerSendError(0, MSG_NO_CHANNEL));
  const team = encodeURIComponent(parsed.team);
  if (parsed.kind === 'channel' || parsed.kind === 'group') {
    // 🔴 그룹 DM 이 팀 이름 조회로 풀리는지는 미실측 — 실패하면 «아직 못 보낸다»로 알린다.
    const notFound = parsed.kind === 'channel' ? MSG_NO_CHANNEL : MSG_UNSUPPORTED;
    const path = MM_API_PREFIX + 'teams/name/' + team + '/channels/name/' + encodeURIComponent(parsed.name);
    return mmJson(fetchImpl, token, path, null, notFound).then((data) => idOf(data, notFound));
  }
  // DM 3종 — 내 id 와 상대 id 로 1:1 채널을 연다(이미 있으면 그 채널이 돌아온다).
  const mePromise = mmJson(fetchImpl, token, MM_API_PREFIX + 'users/me');
  let themPromise = null;
  if (parsed.kind === 'dm_userid') {
    themPromise = Promise.resolve(parsed.userId);               // 경로의 26자가 곧 상대 id 다
  } else if (parsed.kind === 'dm_username') {
    themPromise = mmJson(fetchImpl, token, MM_API_PREFIX + 'users/username/' + encodeURIComponent(parsed.username))
      .then((user) => idOf(user, MSG_NO_CHANNEL));
  } else if (parsed.kind === 'dm_email') {
    themPromise = mmJson(fetchImpl, token, MM_API_PREFIX + 'users/email/' + encodeURIComponent(parsed.email))
      .then((user) => idOf(user, MSG_NO_CHANNEL));
  } else {
    return Promise.reject(new StickerSendError(0, MSG_NO_CHANNEL));
  }
  return Promise.all([mePromise, themPromise]).then((pair) => {
    const meId = idOf(pair[0], MSG_NO_CHANNEL);
    const themId = pair[1];
    if (typeof themId !== 'string' || !themId) throw new StickerSendError(0, MSG_NO_CHANNEL);
    return mmJson(fetchImpl, token, MM_API_PREFIX + 'channels/direct', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify([meId, themId]),
    }).then((channel) => idOf(channel, MSG_UNSUPPORTED));
  });
}

// 스티커 1장 전송: 방 식별 → files 업로드 → posts 게시. 🔴 실패해도 다시 보내지 않는다.
// 수용: files 성공 뒤 posts 가 실패하면 첨부 없는 FileInfo 가 MM 에 남는다(비노출·저장공간만).
export function sendSticker(options) {
  const opts = options || {};
  const fetchImpl = opts.fetchImpl;
  return Promise.resolve().then(() => {
    // 0) 메신저 밖(SSO 이동 중 등)이면 아무 요청도 보내지 않는다.
    const pathname = String((opts.win && opts.win.location && opts.win.location.pathname) || '');
    if (pathname.indexOf('/mm/') !== 0) throw new StickerSendError(0, MSG_NO_SESSION);
    const parsed = parseMmChannelPath(pathname);
    if (!parsed) throw new StickerSendError(0, MSG_NO_CHANNEL);
    const csrf = readMmCsrf(opts.doc);
    if (!csrf) throw new StickerSendError(0, MSG_NO_SESSION);
    return resolveChannelId(parsed, fetchImpl, csrf).then((channelId) => {
      const form = new FormData();
      form.append('files', opts.blob, opts.filename);
      // 🔴 FormData 에는 Content-Type 을 직접 넣지 않는다 — 브라우저가 boundary 를 붙인다.
      return mmJson(fetchImpl, csrf, MM_API_PREFIX + 'files?channel_id=' + encodeURIComponent(channelId), {
        method: 'POST',
        body: form,
      }).then((data) => {
        const infos = (data && data.file_infos) || [];
        const fileId = infos[0] && infos[0].id;
        if (!fileId) throw new StickerSendError(0, MSG_FAILED);
        return mmOk(fetchImpl, csrf, MM_API_PREFIX + 'posts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ channel_id: channelId, message: '', file_ids: [fileId] }),
        });
      }).then(() => ({ ok: true }));
    });
  }).catch((err) => {
    if (err instanceof StickerSendError) throw err;
    // fetch «바깥»에서 난 예외(win.location·doc.cookie getter 의 SecurityError 등).
    throw new StickerSendError(0, MSG_FAILED);
  });
}

// ── 입력창 위 첨부 대기열에 올리기(게시하지 않는다) ──────────────────────────
// 설계: `patch_mm_sticker_stage_before_send.md`(마스터 10-16 «선택하고 입력창 위에 띄운다음에 엔터나
// 전송 누르면 스티커만 전송 … 텍스트를 입력한뒤 엔터나 전송 누르면 둘다 한번에 전송»
// + «다시 고르면 추가되는게 아니라 교체되는 형식»).
// 선례 = `mmExcelPaste.js` [이미지로] — «파일 1개만 든» 합성 paste 를 입력창에 쏘면 MM FileUpload 가
// 첨부 대기열에 올리고, 게시는 사용자가 엔터/전송으로 한다(텍스트가 있으면 게시물 1개로 함께 나간다).
// 🔴 MM FileUpload 는 text/plain 이 비어 있지 않으면 파일을 올리지 않는다 → text 는 넣지 않는다.
// 🔴 생성자는 `doc.defaultView`(iframe 쪽) 것을 쓴다(선례 `mmExcelPaste.js:17-20`).
// 🔴 즉시 게시 폴백은 없다 — 대기열에 못 올리면 문구만 띄운다(sendSticker 를 부르지 않는다).
export const REPLY_TEXTBOX_SEL = '#reply_textbox';
export const STICKER_FILE_PREFIX = 'sticker_';
export const REPLACE_WAIT_MS = 1000;
export const REPLACE_POLL_MS = 50;
// MM 11.11 첨부 대기열 DOM(감싸개 · 올라간 파일 · 올리는 중인 파일 · 이름 · X).
const PREVIEW_CONTAINER_SEL = '.file-preview__container';
const PREVIEW_ITEM_SEL = '[data-testid="file-preview-item"], .file-preview[data-client-id]';
const PREVIEW_NAME_SEL = '.post-image__name, .post-image__filename';
const PREVIEW_REMOVE_SEL = 'a.file-preview__remove';

const MSG_NO_TEXTBOX = '입력창을 찾지 못했습니다 — 메신저를 새로고침해 주세요.';
const MSG_STAGE_FAILED = '스티커를 입력창에 올리지 못했습니다.';
const MSG_REPLACE_FAILED = '이전 스티커를 빼지 못했습니다 — 입력창 위 스티커의 X 를 눌러 주세요.';

function findTextbox(doc) {
  try { return (doc && doc.querySelector(TEXTBOX_SEL)) || null; } catch (e) { return null; }
}

// #post_textbox 에서 위로 올라가며 «대기열 감싸개를 품고 스레드 입력창은 품지 않는» 첫 조상.
// 🔴 스레드 입력창을 품은 조상부터는 위로 갈수록 계속 품으므로 거기서 멈춘다(스레드 대기열 오인 방지).
function previewRootOf(textbox) {
  let node = textbox.parentElement;
  for (let depth = 0; node && depth < MAX_ANCHOR_DEPTH; depth += 1) {
    let hasQueue = false;
    let hasReply = false;
    try {
      hasQueue = !!node.querySelector(PREVIEW_CONTAINER_SEL);
      hasReply = !!node.querySelector(REPLY_TEXTBOX_SEL);
    } catch (e) { return null; }
    if (hasReply) return null;
    if (hasQueue) return node;
    node = node.parentElement;
  }
  return null;
}

// 대기열 항목 1개의 보이는 파일 이름(앞뒤 공백 제거). MM 은 긴 이름을 줄여(… ) 그린다.
function itemNameOf(item) {
  try {
    const label = item.querySelector(PREVIEW_NAME_SEL);
    return String((label && label.textContent) || '').trim();
  } catch (e) { return ''; }
}

// 대기열 항목 중 이름이 'sticker_' 로 시작하는 것만(캡처·문서 등 다른 첨부는 건드리지 않는다).
function stagedStickers(root) {
  const out = [];
  let list = [];
  try { list = root.querySelectorAll(PREVIEW_ITEM_SEL); } catch (e) { return out; }
  for (let i = 0; i < list.length; i += 1) {
    if (itemNameOf(list[i]).indexOf(STICKER_FILE_PREFIX) === 0) out.push(list[i]);
  }
  return out;
}

function isAttached(el) {
  try {
    if (typeof el.isConnected === 'boolean') return el.isConnected;
    return !!(el.ownerDocument && el.ownerDocument.contains(el));
  } catch (e) { return false; }
}

// 고른 항목들이 DOM 에서 빠질 때까지 REPLACE_POLL_MS 간격으로 본다(최대 REPLACE_WAIT_MS).
function waitDetached(items) {
  return new Promise((resolve, reject) => {
    let waited = 0;
    const check = () => {
      if (items.every((el) => !isAttached(el))) { resolve(); return; }
      if (waited >= REPLACE_WAIT_MS) { reject(new StickerSendError(0, MSG_REPLACE_FAILED)); return; }
      waited += REPLACE_POLL_MS;
      setTimeout(check, REPLACE_POLL_MS);
    };
    check();
  });
}

// 이미 올라가 있는 스티커를 X 로 뺀다 — 입력창 위 스티커는 늘 한 장.
// 🔴 빠진 것을 확인한 뒤에 올린다 — MM 은 «빼기»와 «올리기»를 같은 draft 로 고치므로 바로 이어 붙이면
//    앞 갱신이 뒤 갱신을 덮을 수 있다(추정 · 순서를 보장하는 쪽을 택한다).
// 🔴 X 가 없거나 시한 안에 안 빠지면 올리지 않는다(두 장이 되는 것보다 안 올리는 쪽).
function removeStagedStickers(textbox) {
  const root = previewRootOf(textbox);
  if (!root) return Promise.resolve();
  const items = stagedStickers(root);
  if (!items.length) return Promise.resolve();
  const removers = [];
  for (let i = 0; i < items.length; i += 1) {
    let x = null;
    try { x = items[i].querySelector(PREVIEW_REMOVE_SEL); } catch (e) { x = null; }
    if (!x || typeof x.click !== 'function') return Promise.reject(new StickerSendError(0, MSG_REPLACE_FAILED));
    removers.push(x);
  }
  try {
    removers.forEach((x) => { x.click(); });
  } catch (e) {
    return Promise.reject(new StickerSendError(0, MSG_REPLACE_FAILED));
  }
  return waitDetached(items);
}

// Blob 에 형식이 없으면 확장자로 정한다(gif·webp 는 그대로 — 움직이는 스티커가 png 로 둔갑하지 않게).
function stickerMime(blob, filename) {
  const own = blob && typeof blob.type === 'string' ? blob.type : '';
  if (own) return own;
  const name = String(filename || '').toLowerCase();
  const dot = name.lastIndexOf('.');
  const ext = dot >= 0 ? name.slice(dot + 1) : '';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'webp') return 'image/webp';
  return 'image/png';
}

// 스티커를 MM 첨부 대기열에 올린다 — 게시하지 않는다(게시는 사용자가 엔터/전송으로).
// 이미 올라가 있는 스티커(sticker_ 로 시작하는 첨부)는 먼저 빼고 올린다 — 입력창 위 스티커는 늘 한 장.
// 반환: Promise<true> = MM 이 받았다(대기열에 올라감) · 그 밖은 StickerSendError 로 reject.
// (10-16 미리보기) 교체 동안 doc.__gwStickerStageHold 로 미리보기를 붙잡고(깜빡임 방지), 끝나면 풀고
// doc.__gwStickerStageSync 를 부른다(실패로 끝나 더 이상 변이가 없어도 미리보기를 정리한다 · setupMmStickerStage).
export async function stageSticker({ doc, blob, filename } = {}) {
  const first = findTextbox(doc);
  if (!first) throw new StickerSendError(0, MSG_NO_TEXTBOX);
  setStageHold(doc, true);
  try {
    await removeStagedStickers(first);
    // 빼는 사이 MM 이 입력창을 다시 그렸을 수 있다 → 다시 찾는다.
    const textbox = findTextbox(doc);
    if (!textbox) throw new StickerSendError(0, MSG_NO_TEXTBOX);
    let view = null;
    try { view = doc.defaultView || null; } catch (e) { view = null; }
    const FileCtor = view && view.File;
    const DT = view && view.DataTransfer;
    const CE = view && view.ClipboardEvent;
    if (!FileCtor || !DT || !CE) throw new StickerSendError(0, MSG_STAGE_FAILED);
    let ev = null;
    try {
      const file = new FileCtor([blob], filename, { type: stickerMime(blob, filename) });
      const dt = new DT();
      dt.items.add(file);                                   // 🔴 text/plain·text/html 은 넣지 않는다
      ev = new CE('paste', { clipboardData: dt, bubbles: true, cancelable: true });
    } catch (e) {
      throw new StickerSendError(0, MSG_STAGE_FAILED);
    }
    let untouched = true;
    try { untouched = textbox.dispatchEvent(ev) === true; } catch (e) { untouched = true; }
    // true = 아무도 preventDefault 안 함 = MM 이 받지 않았다.
    if (untouched) throw new StickerSendError(0, MSG_STAGE_FAILED);
    // MM 이 받았을 때만 미리보기 그림(객체 URL)을 기록한다 — 실패 경로에서는 만들지 않는다.
    rememberStagedSticker(doc, filename, blob);
    return true;
  } finally {
    setStageHold(doc, false);
    runStageSync(doc);
  }
}

// contenteditable 안(MM 이 다시 그리는 입력 영역)의 매치는 무시한다.
function anchorIn(root, selector) {
  let list = [];
  try { list = root.querySelectorAll(selector || BUTTON_ANCHOR_SEL); } catch (e) { return null; }
  for (let i = 0; i < list.length; i += 1) {
    const el = list[i];
    let editable = false;
    try { editable = !!(el.closest && el.closest('[contenteditable="true"], [contenteditable=""]')); } catch (e) { editable = false; }
    if (!editable) return el;
  }
  return null;
}

// el 을 품은 parent 의 «직계 자식». el 이 parent 밖이면 null.
function directChildOf(parent, el) {
  let child = el;
  while (child && child.parentElement !== parent) child = child.parentElement;
  return child;
}

// a 의 조상 사슬을 올라가며 b 도 품는 첫 요소(= 가장 가까운 공통 조상). a 자신은 후보가 아니다.
function commonAncestor(a, b) {
  let node = a.parentElement;
  while (node) {
    try { if (node.contains(b)) return node; } catch (e) { return null; }
    node = node.parentElement;
  }
  return null;
}

// 앵커를 찾은 탐색 루트 안에서 자리를 고른다.
// 자리 = «#fileUploadButton 과 #emojiPickerButton 의 가장 가까운 공통 조상»(= 액션 줄) 아래,
// 📎 를 품은 직계 자식 바로 뒤 → 버튼이 📎·😊 와 같은 줄에 붙는다.
// 🔴 폴백(😊 없음 · 공통 조상이 #post_textbox 까지 품음 = 입력 영역 전체) = 📎 바로 뒤(같은 부모).
//    폴백 자리의 모양은 보장하지 않는다(MM 업로드 메뉴 래퍼일 수 있다) — 클릭 가로채기는
//    onClick 의 stopPropagation 이 막는다.
function spotFor(root, anchor, textbox) {
  const emoji = anchorIn(root, EMOJI_ANCHOR_SEL);
  const row = emoji ? commonAncestor(anchor, emoji) : null;
  let rowOk = !!row;
  if (rowOk) {
    try { rowOk = !row.contains(textbox); } catch (e) { rowOk = false; }
  }
  if (rowOk) {
    const child = directChildOf(row, anchor);
    if (child) return { parent: row, after: child, anchor };
  }
  const parent = anchor.parentElement;
  return parent ? { parent, after: anchor, anchor } : null;
}

// 앵커(📎)는 #post_textbox 에서 위로 올라가며 찾는다.
// 🔴 #fileUploadButton 을 문서 전체에서 찾지 않는다(스레드 입력창 오인).
function findInsertSpot(doc) {
  let textbox = null;
  try { textbox = doc.querySelector(TEXTBOX_SEL); } catch (e) { textbox = null; }
  if (!textbox) return null;
  let node = textbox.parentElement;
  for (let depth = 0; node && depth < MAX_ANCHOR_DEPTH; depth += 1) {
    const anchor = anchorIn(node, BUTTON_ANCHOR_SEL);
    if (anchor) return spotFor(node, anchor, textbox);
    node = node.parentElement;
  }
  return null;
}

// 버튼 아이콘 — Tabler Icons `sticker`(MIT). 📎·😊 와 같은 «선 아이콘»으로 보이게 인라인 SVG 로 그린다.
// 🔴 createElementNS 전용 네임스페이스 — createElement('svg') 로 만든 요소는 HTML 요소라 안 그려진다.
const SVG_NS = 'http://www.w3.org/2000/svg';
const STICKER_ICON_PATHS = [
  'M20 12l-2 .5a6 6 0 0 1 -6.5 -6.5l.5 -2l8 8',
  'M20 12a8 8 0 1 1 -8 -8',
];
const STICKER_ICON_STYLE = 'width:18px;height:18px;fill:none;stroke:currentColor;display:block';

// 이미 있으면 그대로 둔다 — 옵저버가 «자기 삽입»에 반응해도 DOM 이 안 바뀐다(멱등).
function insertButton(doc, onClick) {
  let existing = null;
  try { existing = doc.getElementById(BUTTON_ID); } catch (e) { existing = null; }
  if (existing) return existing;
  const spot = findInsertSpot(doc);
  if (!spot) return null;
  try {
    const btn = doc.createElement('button');
    btn.id = BUTTON_ID;
    btn.type = 'button';
    btn.setAttribute(MARK_ATTR, '1');
    btn.setAttribute('aria-label', '스티커');
    btn.setAttribute('title', '스티커');
    // 🔴 외부 리소스 금지 — 인라인 SVG(Tabler Icons `sticker` · MIT · https://tabler.io/icons)로 그린다
    // 🔴 버튼과 «같은 문서»의 createElementNS 로 만든다 — createElement('svg') 는 HTML
    //    네임스페이스라 그려지지 않고, innerHTML 은 쓰지 않는다.
    const svg = doc.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('width', '18');
    svg.setAttribute('height', '18');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');   // 색은 버튼 글자색(📎·😊 와 같은 회색·hover)을 따른다
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');      // 읽히는 이름은 버튼의 aria-label 이다
    svg.setAttribute('focusable', 'false');
    // MM 버튼 클래스 CSS 가 svg 의 채움·크기를 덮어도 지키려고 인라인으로 한 번 더 박는다.
    svg.setAttribute('style', STICKER_ICON_STYLE);
    for (let i = 0; i < STICKER_ICON_PATHS.length; i += 1) {
      const path = doc.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', STICKER_ICON_PATHS[i]);
      svg.appendChild(path);
    }
    btn.appendChild(svg);
    // 📎 와 같은 클래스를 쓴다 = 같은 크기·정렬·hover(그 순간 📎 에 붙은 상태 클래스도 함께 온다).
    try {
      const cls = spot.anchor && spot.anchor.className;
      if (typeof cls === 'string' && cls) btn.className = cls;
    } catch (e) { /* */ }
    btn.style.cssText = 'display:inline-flex;align-items:center;justify-content:center;line-height:1';
    btn.addEventListener('click', onClick);
    spot.parent.insertBefore(btn, spot.after.nextSibling);
    return btn;
  } catch (e) { return null; }
}

// doc 당 1회 설치. 반환 = dispose(옵저버·타이머 정리 + 넣은 버튼 제거). 이미 설치돼 있으면 null.
// 🔴 앵커를 못 찾으면 조용히 무동작 — MM 이 바뀌어도 메신저는 그대로 쓸 수 있어야 한다.
export function setupMmStickerButton(doc, options) {
  const opts = options || {};
  if (!doc || !doc.body || doc.__gwStickerBtn) return null;
  const isActive = typeof opts.isActive === 'function' ? opts.isActive : () => true;
  const onOpen = typeof opts.onOpen === 'function' ? opts.onOpen : null;
  doc.__gwStickerBtn = true;
  let disposed = false;
  let timer = null;
  let warned = false;

  const onClick = (ev) => {
    // 🔴 업로드 메뉴 래퍼가 클릭을 가로채 파일 선택창을 열지 않게 먼저 막는다.
    try { ev.preventDefault(); ev.stopPropagation(); } catch (e) { /* */ }
    if (disposed || !onOpen || !isActive()) return;
    let rect = null;
    try {
      const btn = ev.currentTarget || ev.target;
      rect = btn && btn.getBoundingClientRect ? btn.getBoundingClientRect() : null;
    } catch (e) { rect = null; }
    try { onOpen(rect); } catch (e) { /* 패널 쪽 오류가 MM 으로 새지 않게 */ }
  };

  const apply = () => {
    if (disposed || !isActive()) return;
    if (insertButton(doc, onClick)) return;
    if (warned) return;
    warned = true;
    try {
      // eslint-disable-next-line no-console
      console.warn('[gw-sticker] MM 입력창(' + TEXTBOX_SEL + ') 옆 자리를 찾지 못해 스티커 버튼을 넣지 않습니다.');
    } catch (e) { /* */ }
  };

  const kick = () => {
    if (disposed || timer) return;   // 디바운스 — MM 은 타이핑 표시 등으로 프레임마다 변이를 낸다
    timer = setTimeout(() => {
      timer = null;
      if (disposed) return;
      apply();
    }, OBSERVE_DEBOUNCE_MS);
  };

  apply();   // 설치 즉시 1회(옵저버를 기다리지 않는다)

  let obs = null;
  try {
    const MO = (doc.defaultView && doc.defaultView.MutationObserver)
      || (typeof window !== 'undefined' ? window.MutationObserver : null);
    if (MO) {
      // 🔴 입력창이 아니라 doc.body 에 건다 — 채널을 옮기면 입력창 요소 자체가 갈린다.
      obs = new MO(kick);
      obs.observe(doc.body, { childList: true, subtree: true });
    }
  } catch (e) { obs = null; }

  return function dispose() {
    if (disposed) return;
    disposed = true;                 // 남은 콜백·타이머는 여기서부터 무동작
    try { if (obs) obs.disconnect(); } catch (e) { /* */ }
    if (timer) { clearTimeout(timer); timer = null; }
    try {
      const btn = doc.getElementById(BUTTON_ID);
      if (btn && btn.parentNode) btn.parentNode.removeChild(btn);
    } catch (e) { /* */ }
    try { doc.__gwStickerBtn = false; } catch (e) { /* */ }
  };
}

// ── 고른 스티커 미리보기 — 입력창 테두리 «바깥 위»에 그림만 ─────────────────────
// 설계: `patch_mm_sticker_preview_above_input.md`(마스터 10-16 «채팅 입력창 위에 띄우라고 했는데 왜 채팅창 안에
// 들어가냐» · «파일 이름이랑 용량 필요없고 스티커만 보여주면 되는데»).
// 파일은 지금처럼 MM 첨부 대기열에 둔다(엔터/전송·교체는 MM·stageSticker 그대로). 우리는
//  ① 이름이 sticker_ 로 시작하는 대기열 항목의 MM 카드(이름·용량)를 숨기고(표식 + iframe head style),
//  ② 입력 상자(.AdvancedTextEditor) 바로 앞에 그림 한 장 + 작은 X 의 미리보기를 띄운다.
// 🔴 미리보기는 대기열 DOM 의 거울이다 — 매번 DOM 에서 처음부터 계산한다(상태를 들고 있지 않는다).
// 🔴 그림 주소를 못 구하면 미리보기도 숨김도 만들지 않는다 — 보내질 첨부가 화면에서 사라지지 않게.
// 🔴 오류는 조용히 무동작 — MM 카드가 테두리 안에 그대로 보이는 지금 화면으로 돌아간다(파일 머리 규약).
// 🔴 스레드(RHS) 입력창은 범위 밖이다 — previewRootOf 가 #reply_textbox 를 품은 조상에서 멈춘다.
export const STAGE_PREVIEW_ID = 'gw-sticker-stage';            // 우리 미리보기 감싸개 id
export const STAGE_STYLE_ID = 'gw-mm-sticker-stage-css';       // iframe head 에 넣는 style id
export const STAGED_ATTR = 'data-gw-sticker-staged';           // 숨길 MM 대기열 항목 표식
export const STICKER_ONLY_ATTR = 'data-gw-sticker-only';       // 스티커만 든 감싸개 표식
export const STAGE_PREVIEW_PX = 120;                           // 대화 속 스티커 상한(mmStickerSizeCss)과 같은 값
export const EDITOR_SEL = '.AdvancedTextEditor';               // 테두리(__body)를 품은 바깥 줄
export const STAGE_URL_CACHE_MAX = 8;                          // 기억하는 객체 URL 수(넘치면 오래된 것부터 revoke)

const STAGE_BOX_CLASS = 'gw-sticker-stage__box';
const STAGE_X_CLASS = 'gw-sticker-stage__x';
const STAGE_X_LABEL = '스티커 빼기';
const STAGE_NAME_ATTR = 'data-gw-sticker-name';
const STAGE_UPLOADING_ATTR = 'data-uploading';
const PREVIEW_IMAGE_SEL = '.post-image';          // MM 이 그리는 작은 그림(인라인 backgroundImage)
const PREVIEW_UPLOADING_SEL = '.file-preview[data-client-id]';
const UPLOADED_TESTID = 'file-preview-item';

// 높이 0 감싸개(position:relative) 안의 absolute 카드 → 메시지 목록 아래쪽 위에 겹쳐 뜬다
// (입력 상자·목록 배치는 1px 도 안 바뀐다). 왼쪽 24px = .AdvancedTextEditor 좌우 여백(폭 768px 이하는 MM 이 0 → 12px).
export const STAGE_CSS = [
  '[' + STAGED_ATTR + ']{display:none !important}',
  PREVIEW_CONTAINER_SEL + '[' + STICKER_ONLY_ATTR + ']{display:none !important}',
  '#' + STAGE_PREVIEW_ID + '{position:relative;height:0;overflow:visible}',
  '#' + STAGE_PREVIEW_ID + ' .' + STAGE_BOX_CLASS + '{position:absolute;left:24px;bottom:8px;z-index:5;'
    + 'width:' + STAGE_PREVIEW_PX + 'px;height:' + STAGE_PREVIEW_PX + 'px;padding:8px;box-sizing:content-box;'
    + 'background:var(--center-channel-bg);border:1px solid rgba(var(--center-channel-color-rgb),.16);'
    + 'border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,.12)}',
  '#' + STAGE_PREVIEW_ID + ' img{display:block;width:100%;height:100%;object-fit:contain}',
  '#' + STAGE_PREVIEW_ID + '[' + STAGE_UPLOADING_ATTR + '] img{opacity:.55}',
  '#' + STAGE_PREVIEW_ID + ' .' + STAGE_X_CLASS + '{position:absolute;top:-6px;right:-6px;width:22px;height:22px;'
    + 'padding:0;border:0;border-radius:50%;display:flex;align-items:center;justify-content:center;cursor:pointer;'
    + 'background:rgba(var(--center-channel-color-rgb),.72);color:var(--center-channel-bg);font-size:14px;line-height:1}',
  '@media screen and (max-width: 768px){#' + STAGE_PREVIEW_ID + ' .' + STAGE_BOX_CLASS + '{left:12px}}',
].join('');

function setStageHold(doc, on) {
  try { doc.__gwStickerStageHold = !!on; } catch (e) { /* */ }
}

function runStageSync(doc) {
  try {
    const sync = doc && doc.__gwStickerStageSync;
    if (typeof sync === 'function') sync();
  } catch (e) { /* */ }
}

// iframe 쪽 URL(createObjectURL 이 있을 때만). 없으면(jsdom 등) 기록하지 않는다.
function stageUrlApi(doc) {
  try {
    const api = doc && doc.defaultView && doc.defaultView.URL;
    if (api && typeof api.createObjectURL === 'function') return api;
  } catch (e) { /* */ }
  return null;
}

function revokeStageUrl(doc, url) {
  try {
    const api = doc && doc.defaultView && doc.defaultView.URL;
    if (url && api && typeof api.revokeObjectURL === 'function') api.revokeObjectURL(url);
  } catch (e) { /* */ }
}

// stageSticker 가 MM 에 넘긴 Blob 의 객체 URL 을 «파일 이름 → URL» 로 기억한다(움직이는 GIF 그대로 · 올리는 중에도 보인다).
// 같은 이름이면 옛 URL 을 revoke 하고 맨 뒤(최근)로 옮긴다. STAGE_URL_CACHE_MAX 를 넘으면 가장 오래된 것부터 뺀다.
function rememberStagedSticker(doc, filename, blob) {
  const api = stageUrlApi(doc);
  const name = String(filename || '').trim();
  if (!api || !name) return;
  let url = '';
  try { url = api.createObjectURL(blob); } catch (e) { return; }
  if (!url) return;
  try {
    if (!doc.__gwStickerStageUrls) doc.__gwStickerStageUrls = new Map();
    const map = doc.__gwStickerStageUrls;
    if (map.has(name)) {
      revokeStageUrl(doc, map.get(name));
      map.delete(name);
    }
    map.set(name, url);
    while (map.size > STAGE_URL_CACHE_MAX) {
      const oldest = map.keys().next().value;
      revokeStageUrl(doc, map.get(oldest));
      map.delete(oldest);
    }
  } catch (e) { revokeStageUrl(doc, url); }
}

function revokeAllStageUrls(doc) {
  try {
    const map = doc.__gwStickerStageUrls;
    if (map && typeof map.forEach === 'function') map.forEach((url) => { revokeStageUrl(doc, url); });
  } catch (e) { /* */ }
  try { delete doc.__gwStickerStageUrls; } catch (e) { /* */ }
}

// 대기열 이름 → 기억한 객체 URL. 이름이 줄임표(...·…)로 끝나면 그 앞부분으로 시작하는 키 중 가장 최근 것.
function rememberedUrlFor(doc, name) {
  try {
    const map = doc.__gwStickerStageUrls;
    if (!map || typeof map.get !== 'function' || !name) return '';
    if (map.has(name)) return String(map.get(name) || '');
    const tail = /(\.\.\.|…)$/.exec(name);
    if (!tail) return '';
    const head = name.slice(0, name.length - tail[1].length);
    if (!head) return '';
    let found = '';
    map.forEach((url, key) => { if (key.indexOf(head) === 0) found = String(url || ''); });   // 뒤일수록 최근
    return found;
  } catch (e) { return ''; }
}

// MM 썸네일(.post-image 인라인 backgroundImage)의 url(…) 주소. 🔴 같은 출처 경로만 — `/` 로 시작하고
// `//`·`/\` 로 시작하지 않으며 공백·역슬래시·따옴표가 없는 것. 그 밖은 버린다.
function thumbUrlOf(item) {
  let raw = '';
  try {
    const pic = item.querySelector(PREVIEW_IMAGE_SEL);
    raw = String((pic && pic.style && pic.style.backgroundImage) || '');
  } catch (e) { return ''; }
  const m = /url\(\s*(["']?)(.*?)\1\s*\)/.exec(raw);
  const url = m ? m[2] : '';
  return /^\/(?![/\\])[^\s\\"'<>]*$/.test(url) ? url : '';
}

function isUploadingItem(item) {
  try {
    return !!item.matches(PREVIEW_UPLOADING_SEL) && item.getAttribute('data-testid') !== UPLOADED_TESTID;
  } catch (e) { return false; }
}

function removeStagePreview(doc) {
  try {
    const el = doc.getElementById(STAGE_PREVIEW_ID);
    if (el && el.parentNode) el.parentNode.removeChild(el);
  } catch (e) { /* */ }
}

// 표식 정리 — keepItems 에 없는 STAGED_ATTR, keepContainer 가 아닌 STICKER_ONLY_ATTR 를 뗀다(다를 때만 쓴다).
function pruneStageMarks(doc, keepItems, keepContainer) {
  try {
    const staged = doc.querySelectorAll('[' + STAGED_ATTR + ']');
    for (let i = 0; i < staged.length; i += 1) {
      if (keepItems.indexOf(staged[i]) < 0) staged[i].removeAttribute(STAGED_ATTR);
    }
    const only = doc.querySelectorAll('[' + STICKER_ONLY_ATTR + ']');
    for (let i = 0; i < only.length; i += 1) {
      if (only[i] !== keepContainer) only[i].removeAttribute(STICKER_ONLY_ATTR);
    }
  } catch (e) { /* */ }
}

// 미리보기 DOM — iframe 문서의 createElement 로 만든다(innerHTML 금지). 이름·용량·아이콘은 넣지 않는다.
function buildStagePreview(doc, onRemove) {
  const wrap = doc.createElement('div');
  wrap.id = STAGE_PREVIEW_ID;
  const box = doc.createElement('div');
  box.className = STAGE_BOX_CLASS;
  const img = doc.createElement('img');
  img.setAttribute('alt', '스티커');
  img.setAttribute('draggable', 'false');
  const x = doc.createElement('button');
  // 🔴 type=button 필수 — 미리보기가 form#create_post 안에 들어가므로 기본값(submit)이면 X 가 게시를 일으킨다.
  x.setAttribute('type', 'button');
  x.className = STAGE_X_CLASS;
  x.setAttribute('aria-label', STAGE_X_LABEL);
  x.setAttribute('title', STAGE_X_LABEL);
  x.textContent = '×';
  x.addEventListener('click', onRemove);
  box.appendChild(img);
  box.appendChild(x);
  wrap.appendChild(box);
  return wrap;
}

// 동기화 — 대기열 DOM 을 보고 미리보기·표식을 맞춘다. 🔴 모든 쓰기는 «다를 때만» 한다(우리 삽입이 옵저버를
// 한 번 더 깨워도 두 번째 호출은 아무것도 바꾸지 않아 고리가 끝난다).
function syncStage(doc, isActive, onRemove) {
  try {
    if (!isActive()) return;
  } catch (e) { return; }
  try {
    // 빨리 빠지기 — MM 은 프레임마다 변이를 낸다. 평소 비용을 셀렉터 3번으로 묶는다.
    if (!doc.querySelector(PREVIEW_CONTAINER_SEL)
      && !doc.getElementById(STAGE_PREVIEW_ID)
      && !doc.querySelector('[' + STAGED_ATTR + '],[' + STICKER_ONLY_ATTR + ']')) return;
    const textbox = findTextbox(doc);
    let editor = null;
    try { editor = textbox && textbox.closest ? textbox.closest(EDITOR_SEL) : null; } catch (e) { editor = null; }
    const root = textbox ? previewRootOf(textbox) : null;
    const items = root ? stagedStickers(root) : [];     // stageSticker 교체와 같은 판정
    const target = items.length && editor && editor.parentNode ? items[items.length - 1] : null;
    const name = target ? itemNameOf(target) : '';
    const src = target ? (rememberedUrlFor(doc, name) || thumbUrlOf(target)) : '';
    if (!target || !src) {
      // 교체 중(stageSticker)이면 미리보기를 붙잡는다 — 앞 것을 뺀 순간과 새 것이 오는 순간 사이 깜빡임 방지.
      if (!doc.__gwStickerStageHold) removeStagePreview(doc);
      pruneStageMarks(doc, [], null);
      return;
    }
    let preview = doc.getElementById(STAGE_PREVIEW_ID);
    if (!preview) preview = buildStagePreview(doc, onRemove);
    // 자리 = 입력 상자(.AdvancedTextEditor) 바로 앞 형제 = 테두리 바깥 위. React 가 사이에 끼워 넣으면 다시 옮긴다.
    if (editor.previousElementSibling !== preview) editor.parentNode.insertBefore(preview, editor);
    const img = preview.querySelector('img');
    if (img && img.getAttribute('src') !== src) img.setAttribute('src', src);
    if (preview.getAttribute(STAGE_NAME_ATTR) !== name) preview.setAttribute(STAGE_NAME_ATTR, name);
    if (isUploadingItem(target)) {
      if (preview.getAttribute(STAGE_UPLOADING_ATTR) !== '1') preview.setAttribute(STAGE_UPLOADING_ATTR, '1');
    } else if (preview.hasAttribute(STAGE_UPLOADING_ATTR)) {
      preview.removeAttribute(STAGE_UPLOADING_ATTR);
    }
    for (let i = 0; i < items.length; i += 1) {
      if (items[i].getAttribute(STAGED_ATTR) !== '1') items[i].setAttribute(STAGED_ATTR, '1');
    }
    // 감싸개의 모든 항목이 스티커면 감싸개째 숨긴다(빈 감싸개 여백이 남지 않게).
    let container = null;
    try { container = root.querySelector(PREVIEW_CONTAINER_SEL); } catch (e) { container = null; }
    let onlyStickers = false;
    if (container) {
      const all = container.querySelectorAll(PREVIEW_ITEM_SEL);
      onlyStickers = all.length > 0;
      for (let i = 0; i < all.length && onlyStickers; i += 1) {
        if (all[i].getAttribute(STAGED_ATTR) !== '1') onlyStickers = false;
      }
      if (onlyStickers && container.getAttribute(STICKER_ONLY_ATTR) !== '1') container.setAttribute(STICKER_ONLY_ATTR, '1');
    }
    pruneStageMarks(doc, items, onlyStickers ? container : null);
  } catch (e) { /* MM DOM 변동 — 무동작 */ }
}

function applyStageCss(doc) {
  try {
    if (!doc.head) return;
    const found = doc.getElementById(STAGE_STYLE_ID);
    if (found) {
      if (found.textContent !== STAGE_CSS) found.textContent = STAGE_CSS;
      return;
    }
    const st = doc.createElement('style');
    st.id = STAGE_STYLE_ID;
    st.textContent = STAGE_CSS;
    doc.head.appendChild(st);
  } catch (e) { /* */ }
}

function removeStageCss(doc) {
  try {
    const st = doc.getElementById(STAGE_STYLE_ID);
    if (st && st.parentNode) st.parentNode.removeChild(st);
  } catch (e) { /* */ }
}

// doc 당 1회 설치. 반환 = dispose(옵저버 끊기 + 미리보기·표식·style 제거 + 객체 URL revoke). 이미 설치돼 있으면 null.
// 🔴 동기화는 디바운스 없이 MutationObserver 콜백(마이크로태스크)에서 바로 돈다 — MM 이 올리기 끝난 항목을
//    새 요소로 갈아 끼우면 새 요소엔 표식이 없다. 화면을 그리기 전에 붙여야 MM 카드가 잠깐도 안 비친다.
export function setupMmStickerStage(doc, options) {
  const opts = options || {};
  if (!doc || !doc.body || doc.__gwStickerStage) return null;
  const isActive = typeof opts.isActive === 'function' ? opts.isActive : () => true;
  doc.__gwStickerStage = true;
  let disposed = false;
  const active = () => !disposed && !!isActive();

  // 미리보기 X — 그 순간의 sticker_ 항목들의 MM X 만 누른다(스티커 아닌 첨부는 누르지 않는다).
  // 🔴 미리보기를 먼저 지우지 않는다 — MM 이 항목을 빼면 변이 → 동기화가 지운다. 안 빼면 남는다
  //    (화면에서만 사라지고 첨부는 남아 그대로 보내지는 일을 막는다). 포커스는 옮기지 않는다(MM 카드 X 와 같다).
  const onRemove = (ev) => {
    try { ev.preventDefault(); ev.stopPropagation(); } catch (e) { /* */ }
    if (disposed) return;
    try {
      const textbox = findTextbox(doc);
      const root = textbox ? previewRootOf(textbox) : null;
      const items = root ? stagedStickers(root) : [];
      for (let i = 0; i < items.length; i += 1) {
        let x = null;
        try { x = items[i].querySelector(PREVIEW_REMOVE_SEL); } catch (e) { x = null; }
        if (x && typeof x.click === 'function') {
          try { x.click(); } catch (e) { /* */ }
        }
      }
    } catch (e) { /* */ }
  };

  const sync = () => { if (!disposed) syncStage(doc, active, onRemove); };

  applyStageCss(doc);
  try { doc.__gwStickerStageSync = sync; } catch (e) { /* */ }
  sync();   // 설치 즉시 1회(옵저버를 기다리지 않는다)

  let obs = null;
  try {
    const MO = (doc.defaultView && doc.defaultView.MutationObserver)
      || (typeof window !== 'undefined' ? window.MutationObserver : null);
    if (MO) {
      // 🔴 입력창이 아니라 doc.body 에 건다 — 채널을 옮기면 입력창 요소 자체가 갈린다. 속성 변화는 보지 않는다.
      obs = new MO(sync);
      obs.observe(doc.body, { childList: true, subtree: true, characterData: true });
    }
  } catch (e) { obs = null; }

  return function dispose() {
    if (disposed) return;
    disposed = true;                 // 남은 콜백은 여기서부터 무동작
    try { if (obs) obs.disconnect(); } catch (e) { /* */ }
    removeStagePreview(doc);
    pruneStageMarks(doc, [], null);
    removeStageCss(doc);
    revokeAllStageUrls(doc);
    try {
      if (doc.__gwStickerStageSync === sync) delete doc.__gwStickerStageSync;
    } catch (e) { /* */ }
    try { doc.__gwStickerStage = false; } catch (e) { /* */ }
  };
}
